/**
 * S103 — Conectividad WhatsApp multi-canal (simple).
 * - Meta: Embedded Signup (1 clic) — NO forzar tokens manuales
 * - Gupshup / Twilio: si ya están, NO obliga Meta
 * - Manual Meta: solo «avanzado»
 */
import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api, { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PantallaCargando } from '@/components/Cargando';
import MetaWhatsappEmbeddedSignup from '@/components/integrations/MetaWhatsappEmbeddedSignup';
import {
  MessageCircle, CheckCircle2, AlertTriangle, Copy, Loader2,
  RefreshCw, Link2, Shield, Phone, ChevronDown, ChevronUp, Zap,
} from 'lucide-react';

const CHANNEL_HELP = {
  meta_whatsapp: {
    title: 'Meta Cloud (oficial)',
    blurb: 'Ventana de Facebook, eliges el número y listo. Sirve para plantillas que Meta revisa.',
    forTemplates: 'Sí — aquí se mandan a revisión de Meta',
  },
  gupshup: {
    title: 'Gupshup',
    blurb: 'Si ya lo tienes en Integraciones, no necesitas Meta. Las plantillas se aprueban en Gupshup.',
    forTemplates: 'No usa revisión Meta — se gestionan en Gupshup',
  },
  twilio_whatsapp: {
    title: 'Twilio WhatsApp',
    blurb: 'Si ya lo tienes en Integraciones, no necesitas Meta. Content Templates en Twilio.',
    forTemplates: 'No usa revisión Meta — se gestionan en Twilio',
  },
};

export default function WhatsAppConexion() {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [readiness, setReadiness] = useState(null);
  const [checkingReady, setCheckingReady] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [activatingPlatform, setActivatingPlatform] = useState(false);
  const [form, setForm] = useState({
    phone_number_id: '',
    access_token: '',
    business_id: '',
    display_name: '',
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/tenant/whatsapp/business/status');
      setStatus(data);
      const meta = (data.senders || []).find(s => s.connected && (s.family === 'meta' || s.provider_key?.includes('meta') || s.provider_key === 'whatsapp_business'));
      if (meta?.id) {
        try {
          const r = await api.get(`/tenant/whatsapp/business/readiness/${meta.id}`);
          setReadiness(r.data);
        } catch {
          setReadiness(null);
        }
      } else {
        setReadiness(null);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || 'No se pudo cargar el estado');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Copiado');
    } catch {
      toast.error('No se pudo copiar');
    }
  };

  const connectManual = async () => {
    if (!form.phone_number_id.trim() || !form.access_token.trim()) {
      toast.error('Phone Number ID y Access Token son obligatorios');
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post('/tenant/whatsapp/business/connect-manual', {
        phone_number_id: form.phone_number_id.trim(),
        access_token: form.access_token.trim(),
        business_id: form.business_id.trim() || undefined,
        display_name: form.display_name.trim() || undefined,
      });
      toast.success(data.ping || 'WhatsApp Business conectado');
      setForm({ phone_number_id: '', access_token: '', business_id: '', display_name: '' });
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || 'Falló la conexión');
    } finally {
      setSaving(false);
    }
  };

  const testConn = async (id) => {
    setTesting(true);
    try {
      const { data } = await api.post(`/tenant/whatsapp/business/${id}/test`);
      if (data.ok) toast.success(data.message);
      else toast.error(data.message || 'Prueba fallida');
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || 'Error al probar');
    } finally {
      setTesting(false);
    }
  };

  const activatePlatformNumber = async () => {
    setActivatingPlatform(true);
    try {
      const { data } = await api.post('/tenant/whatsapp/twilio/activate-platform-number');
      toast.success(data.message || 'Número de CavernicolApp activado');
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || 'No hay número disponible o tu plan no lo incluye');
    } finally {
      setActivatingPlatform(false);
    }
  };

  const recheckReady = async (id) => {
    setCheckingReady(true);
    try {
      const { data } = await api.get(`/tenant/whatsapp/business/readiness/${id}`);
      setReadiness(data);
      if (data.ready_for_templates) toast.success('Listo para plantillas Meta');
      else toast.message(data.steps_left?.[0] || data.message);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setCheckingReady(false);
    }
  };

  if (loading) return <PantallaCargando />;

  const senders = status?.senders || [];
  const connectedSenders = senders.filter(s => s.connected);
  const readySend = !!status?.ready_for_send;
  const readyMetaTpl = !!status?.ready_for_meta_templates;
  const guidance = status?.guidance || {};
  const metaActive = status?.active;
  const webhookUrl = status?.webhook?.url || '';
  const embeddedOk = !!status?.embedded_signup?.available;

  return (
    <div className="max-w-2xl space-y-5" data-testid="whatsapp-conexion">
      <div>
        <h2 className="text-base font-semibold text-[#0F172A] flex items-center gap-2">
          <Link2 className="w-4 h-4 text-[#128C7E]" /> Cómo envías WhatsApp
        </h2>
        <p className="text-xs text-[#64748B] mt-1">
          Elige un canal. <strong>Si ya tienes Gupshup o Twilio, no necesitas Meta.</strong>
          Meta solo hace falta si quieres plantillas oficiales Cloud API (revisión de Meta).
        </p>
      </div>

      {/* Qué necesitas según la función */}
      <div className="rounded-2xl border border-[#E2E8F0] bg-white p-4 space-y-2" data-testid="wa-what-you-need">
        <p className="text-xs font-bold text-[#0F172A]">¿Qué quieres hacer?</p>
        <div className="grid sm:grid-cols-2 gap-2 text-[11px]">
          <div className={`rounded-xl border p-3 ${readyMetaTpl ? 'border-emerald-200 bg-emerald-50/50' : 'border-[#E2E8F0]'}`}>
            <p className="font-semibold text-[#0F172A]">Mandar plantilla a revisión Meta</p>
            <p className="text-[#64748B] mt-1">{guidance.templates_review}</p>
          </div>
          <div className={`rounded-xl border p-3 ${readySend ? 'border-emerald-200 bg-emerald-50/50' : 'border-[#E2E8F0]'}`}>
            <p className="font-semibold text-[#0F172A]">Enviar mensajes / campañas</p>
            <p className="text-[#64748B] mt-1">{guidance.mass_send}</p>
          </div>
        </div>
      </div>

      {/* Canales conectados */}
      <div className="space-y-2" data-testid="wa-senders-list">
        <p className="text-xs font-bold text-[#0F172A]">Tus canales</p>
        {connectedSenders.length === 0 ? (
          <div className="rounded-xl border border-amber-200 bg-amber-50/60 px-4 py-3 text-sm text-amber-900 flex gap-2">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            <span>Aún no hay canal de WhatsApp. Elige una opción abajo (lo más fácil es Meta en un clic).</span>
          </div>
        ) : (
          connectedSenders.map(s => (
            <div key={s.id} className="rounded-xl border border-emerald-200 bg-emerald-50/50 p-3 flex flex-wrap items-start justify-between gap-2">
              <div className="flex gap-2 min-w-0">
                <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm font-bold text-[#0F172A]">{s.label}</p>
                  <p className="text-[11px] text-[#64748B]">
                    {s.display_name || s.provider_key}
                    {s.phone_hint ? ` · ${s.phone_hint}` : ''}
                  </p>
                  <p className="text-[10px] text-emerald-800 mt-0.5">
                    {s.capabilities?.how_templates || (s.capabilities?.templates_meta_review
                      ? 'Puede mandar plantillas a Meta'
                      : 'No requiere Meta para plantillas')}
                  </p>
                </div>
              </div>
              <div className="flex gap-1.5">
                {(s.family === 'meta' || s.provider_key?.includes('meta')) && (
                  <Button size="sm" variant="outline" className="h-8 text-xs" disabled={checkingReady}
                    onClick={() => recheckReady(s.id)} data-testid="wa-recheck-ready">
                    {checkingReady ? <Loader2 className="w-3 h-3 animate-spin" /> : <Zap className="w-3 h-3 mr-1" />}
                    Verificar
                  </Button>
                )}
                {(s.family === 'meta' || s.provider_key?.includes('meta')) && (
                  <Button size="sm" className="h-8 text-xs bg-[#128C7E] text-white" disabled={testing}
                    onClick={() => testConn(s.id)}>
                    {testing ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Probar API'}
                  </Button>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Readiness cycle Meta */}
      {readiness && metaActive && (
        <div className="rounded-2xl border border-[#E2E8F0] bg-[#F8FAFC] p-4 space-y-3" data-testid="wa-meta-readiness">
          <p className="text-xs font-bold text-[#0F172A]">Listo para usar Meta (después de conectar el número)</p>
          {readiness.message && (
            <p className="text-[11px] text-[#64748B]">{readiness.message}</p>
          )}
          <ul className="space-y-2">
            {(readiness.business_checklist || []).map((item) => (
              <li key={item.id} className="flex gap-2 text-[11px]">
                {item.done === true && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0 mt-0.5" />}
                {item.done === false && <AlertTriangle className="w-3.5 h-3.5 text-amber-600 shrink-0 mt-0.5" />}
                {item.done == null && <Phone className="w-3.5 h-3.5 text-[#94A3B8] shrink-0 mt-0.5" />}
                <div>
                  <p className="font-semibold text-[#0F172A]">{item.title}</p>
                  <p className="text-[#64748B]">{item.hint}</p>
                </div>
              </li>
            ))}
          </ul>
          {(!readiness.business_checklist || readiness.business_checklist.length === 0) && (
            <ul className="text-[11px] text-[#64748B] space-y-1">
              <li className="flex items-center gap-1">
                {readiness.checks?.api_reachable ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> : <AlertTriangle className="w-3.5 h-3.5 text-amber-600" />}
                API de Meta responde
              </li>
              <li className="flex items-center gap-1">
                {readiness.checks?.has_waba ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> : <AlertTriangle className="w-3.5 h-3.5 text-amber-600" />}
                Cuenta WABA (para plantillas)
              </li>
              <li className="flex items-center gap-1">
                {readiness.checks?.webhooks_subscribed ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> : <AlertTriangle className="w-3.5 h-3.5 text-amber-600" />}
                Webhooks / entrantes
              </li>
            </ul>
          )}
        </div>
      )}

      {/* S105 — Número Cavernicol (pool Twilio, sin pelear Integraciones) */}
      {status?.platform_infrastructure?.can_activate_cavernicol_number && (
        <div className="rounded-2xl border-2 border-[#F22F46]/30 bg-[#FFF5F6] p-4 space-y-2" data-testid="wa-activate-platform">
          <h3 className="text-sm font-semibold text-[#B91C1C]">Activar WhatsApp con número de CavernicolApp</h3>
          <p className="text-[11px] text-[#64748B]">
            Tu plan incluye infraestructura compartida. Un clic asigna un número Twilio del pool
            <strong> sin copiar tokens</strong> ni entrar a developers. (No compra un número nuevo: usa el inventario ya cargado por la plataforma.)
          </p>
          <Button
            className="bg-[#F22F46] hover:bg-[#D91E3A] text-white"
            disabled={activatingPlatform}
            onClick={activatePlatformNumber}
            data-testid="wa-activate-platform-btn"
          >
            {activatingPlatform ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Zap className="w-4 h-4 mr-2" />}
            Activar número de CavernicolApp
          </Button>
        </div>
      )}
      {status?.platform_infrastructure?.twilio_whatsapp_pool && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50/50 px-4 py-3 text-sm text-emerald-900" data-testid="wa-platform-active">
          <CheckCircle2 className="w-4 h-4 inline mr-1" />
          WhatsApp con número CavernicolApp: <strong>{status.platform_infrastructure.twilio_whatsapp_pool.channel_identity}</strong>
        </div>
      )}

      {/* S105 — Voz + WhatsApp misma cuenta Twilio */}
      {(status?.voice_senders || []).some(v => v.connected) && (
        <div className="rounded-2xl border border-[#E2E8F0] bg-white p-4 space-y-2" data-testid="wa-voice-unified">
          <p className="text-xs font-bold text-[#0F172A]">Twilio en tu cuenta (voz + WhatsApp)</p>
          <div className="grid sm:grid-cols-2 gap-2 text-[11px]">
            {(status.voice_senders || []).filter(v => v.connected).map(v => (
              <div key={v.id} className="rounded-lg border border-[#E2E8F0] p-2">
                <p className="font-semibold text-[#0F172A]">Voz</p>
                <p className="text-[#64748B]">{v.display_name || 'Twilio Voice'}</p>
                {v.phone_hint && <p className="text-[#94A3B8]">{v.phone_hint}</p>}
                {v.account_sid_suffix && <p className="text-[10px] text-[#94A3B8]">Cuenta …{v.account_sid_suffix}</p>}
              </div>
            ))}
            {(status.senders || []).filter(s => s.provider_key === 'twilio_whatsapp' && s.connected).map(s => (
              <div key={s.id} className="rounded-lg border border-[#E2E8F0] p-2">
                <p className="font-semibold text-[#0F172A]">WhatsApp</p>
                <p className="text-[#64748B]">{s.display_name || s.label}</p>
                {s.phone_hint && <p className="text-[#94A3B8]">{s.phone_hint}</p>}
              </div>
            ))}
          </div>
          {status.same_twilio_account === true && (
            <p className="text-[11px] text-emerald-700 font-medium">Misma cuenta Twilio para voz y WhatsApp ✓</p>
          )}
          {status.same_twilio_account === false && (
            <p className="text-[11px] text-amber-800">
              Voz y WhatsApp usan cuentas Twilio distintas. Si querías unificar, conecta ambos con el mismo Account SID en Integraciones.
            </p>
          )}
          <Link to="/tenant/integrations?provider=twilio_voice" className="text-[11px] font-semibold text-[#F22F46] underline">
            Gestionar Twilio Voz en Integraciones →
          </Link>
        </div>
      )}

      {/* Conectar Meta — simple path */}
      <div className="bg-white border-2 border-[#128C7E]/40 rounded-2xl p-4 space-y-3" data-testid="wa-meta-simple">
        <div>
          <h3 className="text-sm font-semibold text-[#0F172A]">Conectar Meta en un clic</h3>
          <p className="text-[11px] text-[#64748B] mt-0.5">
            Somos Tech Provider: se abre la ventana de Meta, eliges o registras el número, y vuelve aquí.
            <strong> No tienes que copiar tokens</strong> de developers.facebook.com.
          </p>
        </div>
        <MetaWhatsappEmbeddedSignup
          onConnected={async () => {
            toast.success('WhatsApp Meta conectado');
            await load();
          }}
        />
        {!embeddedOk && (
          <p className="text-[11px] text-amber-800 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
            El admin de plataforma aún no activó el App ID de Embedded Signup.
            Mientras, usa Gupshup/Twilio o la opción avanzada.
          </p>
        )}
      </div>

      {/* Otros canales */}
      <div className="bg-white border border-[#E2E8F0] rounded-2xl p-4 space-y-3" data-testid="wa-other-channels">
        <h3 className="text-sm font-semibold text-[#0F172A]">¿Ya tienes Gupshup o Twilio?</h3>
        <p className="text-[11px] text-[#64748B]">
          Entonces <strong>no hace falta</strong> Conexión Meta. Conéctalos (o revísalos) en Integraciones y úsalos para envíos.
        </p>
        <div className="grid sm:grid-cols-2 gap-2">
          {[
            { key: 'gupshup', name: 'Gupshup', to: '/tenant/integrations?provider=gupshup' },
            { key: 'twilio_whatsapp', name: 'Twilio WhatsApp', to: '/tenant/integrations?provider=twilio_whatsapp' },
          ].map(ch => {
            const on = connectedSenders.some(s => s.provider_key === ch.key);
            return (
              <Link key={ch.key} to={ch.to}
                className={`rounded-xl border p-3 text-left transition-colors ${
                  on ? 'border-emerald-200 bg-emerald-50/40' : 'border-[#E2E8F0] hover:border-[#128C7E]/40'
                }`}
                data-testid={`wa-channel-${ch.key}`}>
                <p className="text-sm font-semibold text-[#0F172A] flex items-center gap-1">
                  {on && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />}
                  {ch.name}
                </p>
                <p className="text-[10px] text-[#64748B] mt-1">
                  {CHANNEL_HELP[ch.key]?.forTemplates}
                </p>
                <p className="text-[10px] font-semibold text-[#128C7E] mt-2">
                  {on ? 'Ya conectado · ver en Integraciones' : 'Conectar en Integraciones →'}
                </p>
              </Link>
            );
          })}
        </div>
        <Button variant="outline" size="sm" className="text-xs" onClick={() => navigate('/tenant/integrations')}>
          Abrir Integraciones
        </Button>
      </div>

      {/* Advanced manual Meta */}
      <div className="bg-white border border-[#E2E8F0] rounded-2xl overflow-hidden">
        <button type="button" onClick={() => setShowAdvanced(v => !v)}
          className="w-full flex items-center justify-between px-4 py-3 text-left"
          data-testid="wa-advanced-toggle">
          <span className="text-xs font-semibold text-[#64748B]">Opción avanzada: tokens manuales de Meta</span>
          {showAdvanced ? <ChevronUp className="w-4 h-4 text-[#94A3B8]" /> : <ChevronDown className="w-4 h-4 text-[#94A3B8]" />}
        </button>
        {showAdvanced && (
          <div className="px-4 pb-4 space-y-3 border-t border-[#E2E8F0] pt-3" data-testid="wa-manual-form">
            <p className="text-[11px] text-[#94A3B8]">
              Solo si tu equipo técnico te pasó Phone Number ID y Access Token.
              El camino normal es el botón de un clic de arriba.
            </p>
            <div>
              <label className="text-[10px] text-[#64748B]">Phone Number ID *</label>
              <Input value={form.phone_number_id}
                onChange={e => setForm(f => ({ ...f, phone_number_id: e.target.value }))}
                placeholder="123456789012345" data-testid="wa-phone-number-id" />
            </div>
            <div>
              <label className="text-[10px] text-[#64748B]">Access Token *</label>
              <Input type="password" value={form.access_token}
                onChange={e => setForm(f => ({ ...f, access_token: e.target.value }))}
                placeholder="EAAB..." data-testid="wa-access-token" />
            </div>
            <div>
              <label className="text-[10px] text-[#64748B]">Business ID / WABA (plantillas Meta)</label>
              <Input value={form.business_id}
                onChange={e => setForm(f => ({ ...f, business_id: e.target.value }))}
                placeholder="Recomendado para plantillas" data-testid="wa-business-id" />
            </div>
            <div>
              <label className="text-[10px] text-[#64748B]">Nombre interno</label>
              <Input value={form.display_name}
                onChange={e => setForm(f => ({ ...f, display_name: e.target.value }))}
                placeholder="WhatsApp principal" />
            </div>
            <Button onClick={connectManual} disabled={saving}
              className="w-full bg-[#0F172A] text-white" data-testid="wa-connect-manual-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Shield className="w-4 h-4 mr-2" />}
              Validar con Meta y guardar
            </Button>
            <div className="space-y-1 pt-2">
              <p className="text-[10px] text-[#94A3B8]">Webhook Meta (solo Cloud API)</p>
              <div className="flex gap-2 items-center">
                <code className="flex-1 text-[10px] bg-[#F8FAFC] border rounded-lg px-2 py-2 break-all">{webhookUrl}</code>
                <Button variant="outline" size="sm" onClick={() => copy(webhookUrl)}><Copy className="w-3.5 h-3.5" /></Button>
              </div>
            </div>
          </div>
        )}
      </div>

      {(readySend || readyMetaTpl) && (
        <div className="flex flex-wrap gap-2">
          {readyMetaTpl && (
            <Button className="bg-[#128C7E] text-white" onClick={() => navigate('/tenant/whatsapp/plantillas')}>
              <MessageCircle className="w-4 h-4 mr-1" /> Ir a plantillas Meta
            </Button>
          )}
          <Button variant="outline" onClick={() => navigate('/tenant/whatsapp/envios')}>
            Ver envíos
          </Button>
          <Button variant="ghost" size="sm" onClick={load} className="text-xs">
            <RefreshCw className="w-3 h-3 mr-1" /> Actualizar
          </Button>
        </div>
      )}
    </div>
  );
}
